import {
	Box,
	Icon,
	IconButton,
	Modal,
	ModalBody,
	ModalContent,
	ModalOverlay,
	Spinner,
	Text,
	Tooltip,
} from '@chakra-ui/react';
import { IconAlertTriangle, IconExclamationCircle, IconX } from '@tabler/icons';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import {
	BI_REPORTS_CONFIG,
	BI_REPORTS_MESSAGES,
} from 'components/reports/biReportEmbed/utils/biReports';
import { biFullscreenManager } from 'contexts/BiFullscreenManager';
import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { IBiReportEmbedProps } from './biReportEmbed.types';
import { useBiEmbed } from './hooks/useBiEmbed';
import { resizeBiEmbed } from './services/biEmbedService';

const LoadingSpinner: React.FC = () => {
	return (
		<Box
			display='flex'
			flexDirection='column'
			alignItems='center'
			justifyContent='center'
			height='400px'
		>
			<Spinner
				size={'lg' as const}
				color='blue.500'
				thickness='4px'
				mb={4}
			/>
			<Text
				textAlign='center'
				color='gray.500'
				fontSize='md'
			>
				{BI_REPORTS_MESSAGES.MSG_BI_LOADING}
			</Text>
		</Box>
	);
};

const ErrorBoundary: React.FC<{
	error: string;
	onRetry: () => void;
}> = ({ error, onRetry }) => {
	return (
		<Box
			display='flex'
			flexDirection='column'
			alignItems='center'
			justifyContent='center'
			textAlign='center'
			height='400px'
			bg='red.50'
			borderRadius='md'
			p={6}
		>
			<Icon
				as={IconExclamationCircle as React.ElementType}
				boxSize='40px'
				mb={4}
				color='red.500'
			/>
			<Text
				fontSize='lg'
				fontWeight='semibold'
				mb={2}
				color='red.800'
			>
				{BI_REPORTS_MESSAGES.MSG_BI_ERROR_TITLE}
			</Text>
			<Text
				maxWidth='sm'
				mb={4}
				color='red.700'
			>
				{error}
			</Text>
			<ButtonComponent
				onClick={onRetry}
				variant='outline'
				size='sm'
				aria-label={BI_REPORTS_MESSAGES.MSG_BI_RETRY}
				title={BI_REPORTS_MESSAGES.MSG_BI_RETRY}
			/>
		</Box>
	);
};

const ManualLoadComponent: React.FC<{
	onLoad: () => void;
}> = ({ onLoad }) => (
	<Box
		display='flex'
		flexDirection='column'
		alignItems='center'
		justifyContent='center'
		height='100%'
	>
		<Text
			textAlign='center'
			color='gray.500'
			fontSize='md'
			mb={4}
		>
			{BI_REPORTS_MESSAGES.MSG_BI_MANUAL_LOAD_INSTRUCTION}
		</Text>
		<ButtonComponent
			onClick={onLoad}
			colorScheme='blue'
			size='md'
			aria-label={BI_REPORTS_MESSAGES.MSG_BI_LOAD_DASHBOARD}
		>
			{BI_REPORTS_MESSAGES.MSG_BI_LOAD_DASHBOARD}
		</ButtonComponent>
	</Box>
);

const TokenWarning: React.FC = () => {
	return (
		<Box
			display='flex'
			alignItems='center'
			p={4}
			bg='orange.50'
			borderRadius='md'
			borderLeft='4px solid'
			borderColor='orange.400'
		>
			<Icon
				as={IconAlertTriangle as React.ElementType}
				color='orange.400'
				mr={3}
			/>
			<Box>
				<Text
					fontWeight='semibold'
					color='orange.800'
				>
					{BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_CONFIGURED}
				</Text>
				<Text
					color='orange.700'
					mt={1}
				>
					{BI_REPORTS_MESSAGES.MSG_BI_TOKEN_CONFIG_INSTRUCTION}
				</Text>
			</Box>
		</Box>
	);
};

const BiReportEmbed: React.FC<IBiReportEmbedProps> = ({
	reportToken,
	containerHeight = BI_REPORTS_CONFIG.DEFAULT_CONTAINER_HEIGHT,
	autoLoad = true,
	onError,
	onLoad,
}) => {
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(
		biFullscreenManager.getIsFullscreenOpen()
	);

	useEffect(() => {
		const unsubscribe = biFullscreenManager.subscribe(isOpen => {
			setIsFullscreenOpen(isOpen);
		});

		const handleOpen = () => setIsFullscreenOpen(true);
		const handleClose = () => setIsFullscreenOpen(false);

		globalThis.addEventListener('bi-fullscreen-open', handleOpen);
		globalThis.addEventListener('bi-fullscreen-close', handleClose);

		return () => {
			unsubscribe();
			globalThis.removeEventListener('bi-fullscreen-open', handleOpen);
			globalThis.removeEventListener('bi-fullscreen-close', handleClose);
		};
	}, []);

	const closeFullscreen = () => {
		biFullscreenManager.closeFullscreen();
	};

	const { isLoading, error, isEmbedded, embedBi, resetEmbed, containerRef } =
		useBiEmbed({
			reportToken,
			containerHeight,
		});

	const {
		isLoading: isLoadingFullscreen,
		error: errorFullscreen,
		isEmbedded: isEmbeddedFullscreen,
		embedBi: embedBiFullscreen,
		resetEmbed: resetEmbedFullscreen,
		containerRef: containerRefFullscreen,
	} = useBiEmbed({
		reportToken,
		containerHeight: '100vh',
	});

	useEffect(() => {
		if (autoLoad && reportToken) {
			embedBi();
		}
	}, [embedBi, autoLoad, reportToken]);

	useEffect(() => {
		if (error && onError) {
			onError(error);
		}
	}, [error, onError]);

	useEffect(() => {
		if (isEmbedded && onLoad) {
			onLoad();
		}
	}, [isEmbedded, onLoad]);

	const applyStylesToChildren = (container: HTMLElement): void => {
		container.style.width = '100%';
		container.style.height = '100%';
		container.style.padding = '0';
		container.style.margin = '0';
		container.style.overflow = 'hidden';
		container.style.boxSizing = 'border-box';

		const parent = container.parentElement;
		if (parent) {
			parent.style.width = '100%';
			parent.style.height = '100%';
			parent.style.padding = '0';
			parent.style.margin = '0';
			parent.style.overflow = 'hidden';
			parent.style.boxSizing = 'border-box';
		}

		const iframes = Array.from(container.querySelectorAll('iframe'));
		const divs = Array.from(container.querySelectorAll('div'));

		const containerRect = container.getBoundingClientRect();
		const containerWidth = containerRect.width || container.offsetWidth;
		const containerHeight = containerRect.height || container.offsetHeight;

		for (const iframe of iframes) {
			iframe.style.width = `${containerWidth}px`;
			iframe.style.height = `${containerHeight}px`;
			iframe.style.minWidth = '100%';
			iframe.style.minHeight = '100%';
			iframe.style.maxWidth = '100%';
			iframe.style.maxHeight = '100%';
			iframe.style.border = 'none';
			iframe.style.display = 'block';
			iframe.style.boxSizing = 'border-box';
			iframe.style.padding = '0';
			iframe.style.margin = '0';
		}

		for (const div of divs) {
			if (div !== container) {
				div.style.width = '100%';
				div.style.height = '100%';
				div.style.padding = '0';
				div.style.margin = '0';
				div.style.boxSizing = 'border-box';
				div.style.overflow = 'hidden';
			}
		}

		const allChildren = Array.from(container.querySelectorAll('*'));
		for (const child of allChildren) {
			const element = child as HTMLElement;
			if (
				element !== container &&
				!iframes.includes(element as HTMLIFrameElement) &&
				!divs.includes(element as HTMLDivElement)
			) {
				element.style.boxSizing = 'border-box';
			}
		}
	};

	const setupBiResize = useCallback(
		(container: HTMLElement, containerId: string): (() => void) => {
			let resizeTimeout: NodeJS.Timeout;
			const handleResize = () => {
				clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(() => {
					applyStylesToChildren(container);
					resizeBiEmbed(containerId);
				}, 150);
			};

			const resizeObserver = new ResizeObserver(() => {
				handleResize();
			});

			resizeObserver.observe(container);

			const handleFullscreenChange = () => {
				setTimeout(() => {
					applyStylesToChildren(container);
					resizeBiEmbed(containerId);
				}, 50);
				setTimeout(() => {
					applyStylesToChildren(container);
					resizeBiEmbed(containerId);
				}, 200);
				setTimeout(() => {
					applyStylesToChildren(container);
					resizeBiEmbed(containerId);
				}, 500);
			};

			globalThis.addEventListener(
				'fullscreenchange',
				handleFullscreenChange
			);
			globalThis.addEventListener(
				'webkitfullscreenchange',
				handleFullscreenChange
			);
			globalThis.addEventListener(
				'mozfullscreenchange',
				handleFullscreenChange
			);
			globalThis.addEventListener(
				'MSFullscreenChange',
				handleFullscreenChange
			);

			setTimeout(() => {
				applyStylesToChildren(container);
				handleResize();
			}, 300);

			const mutationObserver = new MutationObserver(mutations => {
				const hasNewElements = mutations.some(
					mutation => mutation.addedNodes.length > 0
				);
				if (hasNewElements) {
					applyStylesToChildren(container);
				}
			});

			mutationObserver.observe(container, {
				childList: true,
				subtree: true,
			});

			return () => {
				clearTimeout(resizeTimeout);
				resizeObserver.disconnect();
				mutationObserver.disconnect();
				globalThis.removeEventListener(
					'fullscreenchange',
					handleFullscreenChange
				);
				globalThis.removeEventListener(
					'webkitfullscreenchange',
					handleFullscreenChange
				);
				globalThis.removeEventListener(
					'mozfullscreenchange',
					handleFullscreenChange
				);
				globalThis.removeEventListener(
					'MSFullscreenChange',
					handleFullscreenChange
				);
			};
		},
		[]
	);

	useEffect(() => {
		if (!isEmbedded || !containerRef.current) {
			return;
		}

		const container = containerRef.current;
		const containerId = container.id;

		if (!containerId) {
			return;
		}

		return setupBiResize(container, containerId);
	}, [isEmbedded, containerRef, setupBiResize]);

	useEffect(() => {
		if (!isEmbeddedFullscreen || !containerRefFullscreen.current) {
			return;
		}

		const container = containerRefFullscreen.current;
		const containerId = container.id;

		if (!containerId) {
			return;
		}

		return setupBiResize(container, containerId);
	}, [isEmbeddedFullscreen, containerRefFullscreen, setupBiResize]);

	useEffect(() => {
		if (isFullscreenOpen && reportToken) {
			let attempts = 0;
			const maxAttempts = 10;

			const checkAndEmbed = () => {
				if (containerRefFullscreen.current) {
					embedBiFullscreen();
				} else if (attempts < maxAttempts) {
					attempts++;
					setTimeout(checkAndEmbed, 100);
				}
			};

			requestAnimationFrame(() => {
				setTimeout(checkAndEmbed, 150);
			});

			return () => {
				if (isFullscreenOpen) {
					resetEmbedFullscreen();
				}
			};
		}
		return () => {
			if (isFullscreenOpen) {
				resetEmbedFullscreen();
			}
		};
	}, [
		isFullscreenOpen,
		reportToken,
		embedBiFullscreen,
		resetEmbedFullscreen,
		containerRefFullscreen,
	]);

	const handleRetry = (): void => {
		resetEmbed();
		embedBi();
	};

	const handleRetryFullscreen = (): void => {
		resetEmbedFullscreen();
		embedBiFullscreen();
	};

	const handleManualLoad = (): void => {
		embedBi();
	};

	const handleManualLoadFullscreen = (): void => {
		embedBiFullscreen();
	};

	const handleCloseFullscreen = (): void => {
		closeFullscreen();
		resetEmbedFullscreen();

		setTimeout(() => {
			resetEmbed();
			setTimeout(() => {
				embedBi();
			}, 100);
		}, 200);
	};

	const shouldShowTokenWarning =
		!reportToken &&
		error?.includes(BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_CONFIGURED);

	if (shouldShowTokenWarning) {
		return <TokenWarning />;
	}

	interface IRenderEmbedContentProps {
		isLoadingState: boolean;
		errorState: string | null;
		isEmbeddedState: boolean;
		containerRefState: React.RefObject<HTMLDivElement>;
		onRetry: () => void;
		onManualLoad: () => void;
		customHeight?: string;
	}

	const renderEmbedContent = ({
		isLoadingState,
		errorState,
		isEmbeddedState,
		containerRefState,
		onRetry,
		onManualLoad,
		customHeight,
	}: IRenderEmbedContentProps) => (
		<Box
			width='100%'
			height={customHeight || containerHeight}
			minHeight={
				customHeight === '100%'
					? '100%'
					: BI_REPORTS_CONFIG.DEFAULT_MIN_HEIGHT
			}
			maxHeight={customHeight === '100%' ? '100%' : undefined}
			border='1px solid'
			borderColor='gray.200'
			borderRadius='md'
			overflow='hidden'
			bg='white'
			boxShadow='sm'
			position='relative'
			p={0}
			m={0}
			boxSizing='border-box'
			display={customHeight === '100%' ? 'flex' : 'block'}
			flexDirection={customHeight === '100%' ? 'column' : undefined}
			flex={customHeight === '100%' ? '1' : undefined}
		>
			{isLoadingState && <LoadingSpinner />}

			{errorState && !isLoadingState && (
				<ErrorBoundary
					error={errorState}
					onRetry={onRetry}
				/>
			)}

			{!autoLoad &&
				!isLoadingState &&
				!isEmbeddedState &&
				!errorState && <ManualLoadComponent onLoad={onManualLoad} />}

			<Box
				ref={containerRefState}
				width='100%'
				height={isEmbeddedState && !isLoadingState ? '100%' : '0px'}
				minHeight={
					isEmbeddedState && !isLoadingState ? '100%' : undefined
				}
				maxHeight={
					isEmbeddedState && !isLoadingState ? '100%' : undefined
				}
				overflow='hidden'
				position='relative'
				p={0}
				m={0}
				boxSizing='border-box'
				flex={customHeight === '100%' ? '1' : undefined}
				transition={`height ${BI_REPORTS_CONFIG.TRANSITION_DURATION} ${BI_REPORTS_CONFIG.TRANSITION_EASING}`}
				aria-label='BI Report Dashboard'
				aria-live={isLoadingState ? 'polite' : 'off'}
				aria-busy={isLoadingState}
			/>
		</Box>
	);

	return (
		<>
			{renderEmbedContent({
				isLoadingState: isLoading,
				errorState: error,
				isEmbeddedState: isEmbedded,
				containerRefState: containerRef,
				onRetry: handleRetry,
				onManualLoad: handleManualLoad,
			})}

			<Modal
				isOpen={isFullscreenOpen}
				onClose={handleCloseFullscreen}
				size='full'
				isCentered
			>
				<ModalOverlay />
				<ModalContent
					m={0}
					borderRadius={0}
					maxW='100vw'
					maxH='100vh'
					minH='100vh'
					h='100vh'
					position='relative'
					display='flex'
					flexDirection='column'
				>
					<Tooltip
						label='Fechar tela cheia'
						placement='right'
						bg='gray.200'
						color='blue.800'
						hasArrow
					>
						<IconButton
							aria-label='Fechar tela cheia'
							icon={<IconX />}
							size='md'
							position='absolute'
							top={3}
							left={3}
							zIndex={1000}
							bg='white'
							boxShadow='md'
							borderRadius='full'
							_hover={{ bg: 'gray.100' }}
							onClick={handleCloseFullscreen}
						/>
					</Tooltip>
					<ModalBody
						p={0}
						overflow='hidden'
						height='100%'
						minHeight='100%'
						maxHeight='100%'
						flex='1'
						display='flex'
						flexDirection='column'
					>
						{renderEmbedContent({
							isLoadingState: isLoadingFullscreen,
							errorState: errorFullscreen,
							isEmbeddedState: isEmbeddedFullscreen,
							containerRefState: containerRefFullscreen,
							onRetry: handleRetryFullscreen,
							onManualLoad: handleManualLoadFullscreen,
							customHeight: '100%',
						})}
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};

const BiReportEmbedWithSuspense: React.FC<IBiReportEmbedProps> = props => (
	<Suspense fallback={<LoadingSpinner />}>
		<BiReportEmbed {...props} />
	</Suspense>
);

export default BiReportEmbedWithSuspense;
export { BiReportEmbed };
